import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { jobApplicationService } from '../services/jobApplicationService';

const DraftContext = createContext();

export const useDraft = () => {
  const context = useContext(DraftContext);
  if (!context) {
    throw new Error('useDraft must be used within a DraftProvider');
  }
  return context;
};

export const DraftProvider = ({ children }) => {
  const { currentUser, userId, loading: authLoading } = useAuth();

  // Map of jobId (string) → draft object
  const [draftMap, setDraftMap] = useState({});
  const [draftsLoading, setDraftsLoading] = useState(false);
  const isFetchingRef = useRef(false);

  const buildDraftMap = (drafts) => {
    const map = {};
    if (!Array.isArray(drafts)) return map;
    drafts.forEach((d) => {
      const key = String(d.jobId || d.jobPostingId || '');
      if (key) map[key] = d;
    });
    return map;
  };

  const fetchDrafts = useCallback(async () => {
    if (!userId || isFetchingRef.current) return;
    isFetchingRef.current = true;
    setDraftsLoading(true);
    try {
      const result = await jobApplicationService.getDraftApplications(userId);
      if (result.success) {
        let arr = [];
        if (Array.isArray(result.data)) {
          arr = result.data;
        } else if (result.data && typeof result.data === 'object') {
          arr = result.data.drafts || result.data.content || result.data.data || [];
          if (!Array.isArray(arr)) {
            arr = Object.values(result.data).find(Array.isArray) || [];
          }
        }
        setDraftMap(buildDraftMap(arr));
      }
    } catch (err) {
      console.error('DraftContext: Failed to fetch drafts', err);
    } finally {
      isFetchingRef.current = false;
      setDraftsLoading(false);
    }
  }, [userId]);

  // Re-fetch whenever auth state changes
  useEffect(() => {
    if (authLoading) return;
    if (!currentUser) {
      setDraftMap({});
      return;
    }
    fetchDrafts();
  }, [authLoading, currentUser, fetchDrafts]);

  /** Returns true if the user has an in-progress draft for this jobId */
  const hasDraft = useCallback(
    (jobId) => {
      if (!jobId) return false;
      return Boolean(draftMap[String(jobId)]);
    },
    [draftMap]
  );

  /** Returns the draft summary object for this jobId (or null) */
  const getDraftForJob = useCallback(
    (jobId) => {
      if (!jobId) return null;
      return draftMap[String(jobId)] || null;
    },
    [draftMap]
  );

  /** Call this after saving / discarding a draft to refresh global state */
  const refreshDrafts = useCallback(() => {
    fetchDrafts();
  }, [fetchDrafts]);

  const value = {
    draftMap,
    draftsLoading,
    hasDraft,
    getDraftForJob,
    refreshDrafts,
  };

  return <DraftContext.Provider value={value}>{children}</DraftContext.Provider>;
};

export default DraftContext;
